import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ─── breakpoint hook ─── */
function useBreakpoint() {
  const [w, setW] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return {
    isMobile: w < 640,
    isTablet: w >= 640 && w < 1024,
    isDesktop: w >= 1024,
    w,
  };
}

const NAV_LINKS = [
  { label: "Discover", icon: "🔍", sectionId: "discover" },
  { label: "How It Works", icon: "⚡", sectionId: "how-it-works" },
  { label: "For Creators", icon: "🚀", sectionId: "for-creators" },
  { label: "Enterprise", icon: "🏢", sectionId: "enterprise" },
  { label: "Blog", icon: "📝", sectionId: "cta" },
];

const CAMPAIGNS = [
  {
    id: 1,
    title: "NovaBike: The World's Lightest Electric Cargo Bike",
    category: "Technology",
    location: "Berlin, Germany",
    raised: 284500,
    goal: 350000,
    backers: 3142,
    daysLeft: 18,
    flag: "🇩🇪",
    image:
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&q=80",
    tag: "Staff Pick",
    tagColor: "#22D3EE",
  },
  {
    id: 2,
    title: "SOLARA: Off-Grid Solar Shelter for Remote Communities",
    category: "Sustainability",
    location: "Cape Town, South Africa",
    raised: 97200,
    goal: 150000,
    backers: 1289,
    daysLeft: 31,
    flag: "🇿🇦",
    image:
      "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=700&q=80",
    tag: "Trending",
    tagColor: "#F472B6",
  },
  {
    id: 3,
    title: "Loom & Thread: Handwoven Textiles from Peruvian Artisans",
    category: "Culture",
    location: "Cusco, Peru",
    raised: 41800,
    goal: 60000,
    backers: 874,
    daysLeft: 9,
    flag: "🇵🇪",
    image:
      "https://images.unsplash.com/photo-1464207687429-7505649dae38?w=700&q=80",
    tag: "Ending Soon",
    tagColor: "#FB923C",
  },
  {
    id: 4,
    title: "MindStack: AI Flashcards That Adapt to Your Brain",
    category: "Education",
    location: "Toronto, Canada",
    raised: 523000,
    goal: 500000,
    backers: 8711,
    daysLeft: 44,
    flag: "🇨🇦",
    image:
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=700&q=80",
    tag: "Funded ✓",
    tagColor: "#4ADE80",
  },
  {
    id: 5,
    title: "AERO DESK Pro — Floating Modular Workspace",
    category: "Design",
    location: "Tokyo, Japan",
    raised: 189400,
    goal: 200000,
    backers: 2034,
    daysLeft: 22,
    flag: "🇯🇵",
    image:
      "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=700&q=80",
    tag: "Staff Pick",
    tagColor: "#22D3EE",
  },
  {
    id: 6,
    title: "DeepSea Drones: Ocean Plastic Monitoring Network",
    category: "Environment",
    location: "Oslo, Norway",
    raised: 316700,
    goal: 400000,
    backers: 4480,
    daysLeft: 27,
    flag: "🇳🇴",
    image:
      "https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=700&q=80",
    tag: "Trending",
    tagColor: "#F472B6",
  },
];

const STATS = [
  { value: "$1.2B+", label: "Total Funded" },
  { value: "92 Countries", label: "Global Reach" },
  { value: "6.8M+", label: "Backers" },
  { value: "24,000+", label: "Projects Launched" },
];

const CATEGORIES = [
  "All",
  "Technology",
  "Sustainability",
  "Culture",
  "Education",
  "Design",
  "Environment",
];

const TESTIMONIALS = [
  {
    name: "Elara Voss",
    role: "Founder, SOLARA",
    location: "Cape Town, SA",
    text: "HEXAFUND gave us the global platform we needed. We raised $97K from 38 countries in 3 weeks — our local bank couldn't even imagine that.",
    initials: "EV",
  },
  {
    name: "Kenji Murakami",
    role: "Designer & Creator",
    location: "Tokyo, Japan",
    text: "The campaign analytics are world-class. I knew exactly who was backing me and why. That data shaped our entire product roadmap.",
    initials: "KM",
  },
  {
    name: "Sofia Marín",
    role: "Serial Backer",
    location: "Barcelona, Spain",
    text: "I've backed 60+ projects on HEXAFUND. The verification system and milestone tracking make it the only platform I trust with my money.",
    initials: "SM",
  },
];

const PARTNERS = [
  "Forbes",
  "TechCrunch",
  "Bloomberg",
  "The Guardian",
  "Wired",
  "Fast Company",
];

const PLATFORM_STATS = [
  { icon: "💰", label: "Fundraise at 0% Platform Fee" },
  { icon: "⚡", label: "Quick Funds Disbursal" },
  { icon: "🏥", label: "40,000+ Patients Supported" },
  { icon: "❤️", label: "2+ Lakh Lives Impacted" },
];

const MEDICAL_CATEGORIES = [
  { icon: "🫘", title: "Kidney Transplant", raised: "₹3 Lakh", color: "#60A5FA" },
  { icon: "🎗️", title: "Cancer Crowdfunding", raised: "₹5 Crore", color: "#F472B6" },
  { icon: "🦴", title: "Bone Marrow Transplant", raised: "₹2 Lakh", color: "#A78BFA" },
  { icon: "❤️", title: "Heart Transplant", raised: "₹4 Lakh", color: "#FB923C" },
  { icon: "🫁", title: "Covid-19 Support", raised: "₹1 Lakh", color: "#34D399" },
];

const FAQS = [
  { q: "What is crowdfunding?", a: "Crowdfunding is the process of raising funds with the help of people across the world to pay for medical treatment, education, social causes, or creative projects." },
  { q: "Is crowdfunding legal in India?", a: "Yes, crowdfunding is completely legal in India for personal, social, and charitable causes. HEXAFUND complies with all applicable regulations." },
  { q: "How much does it cost to raise funds on HEXAFUND?", a: "It is completely free to start a fundraiser. HEXAFUND charges 0% platform fee, so all donations go directly to you." },
  { q: "Do we need to return the funds raised on donation-based crowdfunding platforms?", a: "No. Funds raised through donation-based crowdfunding are gifts from donors and do not need to be repaid." },
  { q: "What do I need to start a fundraiser on HEXAFUND?", a: "You need a valid ID, a bank account, and a compelling story. Our team will guide you through the entire setup process." },
  { q: "When will I receive the raised funds?", a: "Funds are disbursed quickly — typically within 2–5 working days after the withdrawal request is submitted." },
  { q: "What makes me ineligible to raise money online on donation platforms?", a: "Fundraisers promoting illegal activities, hate speech, or fraudulent claims are ineligible. All campaigns are reviewed for authenticity." },
  { q: "Why shouldn't I have more questions about online crowdfunding?", a: "We encourage questions! Our support team is available 24/7 to help with any doubts about your fundraising journey." },
];

const FOOTER_COLS = [
  {
    title: "Platform",
    links: [
      "Start a Campaign",
      "Discover Projects",
      "How It Works",
      "Pricing",
      "Creator Resources",
    ],
  },
  {
    title: "Company",
    links: [
      "About HEXAFUND",
      "Careers",
      "Press Kit",
      "Partner Program",
      "Blog",
    ],
  },
  {
    title: "Legal",
    links: [
      "Terms of Service",
      "Privacy Policy",
      "Cookie Settings",
      "Security",
      "Accessibility",
    ],
  },
];

/* ─── sub-components ─── */
function ProgressBar({ raised, goal }) {
  const pct = Math.min(100, Math.round((raised / goal) * 100));
  const over = raised > goal;
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 7,
        }}
      >
        <span
          style={{
            color: over ? "#4ADE80" : "#7DD3FC",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          ${raised >= 1000 ? (raised / 1000).toFixed(0) + "K" : raised} raised
        </span>
        <span style={{ color: "#64748B", fontSize: 12, fontWeight: 600 }}>
          {pct}%
        </span>
      </div>
      <div
        style={{
          background: "#0f2744",
          borderRadius: 99,
          height: 5,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: over
              ? "linear-gradient(90deg,#4ADE80,#22D3EE)"
              : "linear-gradient(90deg,#2563EB,#22D3EE)",
            borderRadius: 99,
          }}
        />
      </div>
    </div>
  );
}

function Tag({ text, color }) {
  return (
    <span
      style={{
        background: color + "22",
        color,
        border: `1px solid ${color}55`,
        borderRadius: 6,
        padding: "3px 9px",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 1,
        textTransform: "uppercase",
      }}
    >
      {text}
    </span>
  );
}

function CampaignCard({ c }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#0B1E36",
        border: `1px solid ${hov ? "#2563EB66" : "#132440"}`,
        borderRadius: 18,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all .35s cubic-bezier(.4,0,.2,1)",
        transform: hov ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hov ? "0 24px 60px #0009" : "0 2px 16px #0005",
      }}
    >
      <div style={{ position: "relative", height: 210, overflow: "hidden" }}>
        <img
          src={c.image}
          alt={c.title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform .6s ease",
            transform: hov ? "scale(1.08)" : "scale(1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top,#0B1E36 0%,transparent 55%)",
          }}
        />
        <div style={{ position: "absolute", top: 14, left: 14 }}>
          <Tag text={c.tag} color={c.tagColor} />
        </div>
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "#0B1E36CC",
            border: "1px solid #132440",
            borderRadius: 8,
            padding: "4px 10px",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 14 }}>{c.flag}</span>
          <span style={{ color: "#94A3B8", fontSize: 11, fontWeight: 600 }}>
            {c.location}
          </span>
        </div>
      </div>
      <div style={{ padding: "20px 22px 24px" }}>
        <div style={{ marginBottom: 8 }}>
          <span
            style={{
              color: "#2563EB",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            {c.category}
          </span>
        </div>
        <h3
          style={{
            color: "#F1F5F9",
            fontSize: 17,
            fontWeight: 700,
            margin: "0 0 16px",
            lineHeight: 1.45,
            fontFamily: "'Cormorant Garant',Georgia,serif",
          }}
        >
          {c.title}
        </h3>
        <ProgressBar raised={c.raised} goal={c.goal} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 18,
            flexWrap: "wrap",
            gap: 4,
          }}
        >
          <span style={{ color: "#64748B", fontSize: 12 }}>
            <strong style={{ color: "#94A3B8" }}>
              {c.backers.toLocaleString()}
            </strong>{" "}
            backers
          </span>
          <span style={{ color: "#64748B", fontSize: 12 }}>
            Goal:{" "}
            <strong style={{ color: "#94A3B8" }}>
              ${(c.goal / 1000).toFixed(0)}K
            </strong>
          </span>
          <span
            style={{
              color: c.daysLeft <= 10 ? "#FB923C" : "#64748B",
              fontSize: 12,
              fontWeight: c.daysLeft <= 10 ? 700 : 400,
            }}
          >
            {c.daysLeft} days left
          </span>
        </div>
        <button
          style={{
            width: "100%",
            padding: "11px 0",
            background: hov
              ? "linear-gradient(90deg,#1D4ED8,#2563EB)"
              : "transparent",
            color: hov ? "#fff" : "#7DD3FC",
            border: `1.5px solid ${hov ? "transparent" : "#1e3a5f"}`,
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            transition: "all .3s ease",
          }}
        >
          Back this project →
        </button>
      </div>
    </div>
  );
}

/* ─── FAQ ─── */
function FaqSection({ isMobile, px }) {
  const [open, setOpen] = useState(null);
  return (
    <section
      style={{
        padding: `${isMobile ? 48 : 80}px ${px}`,
        background: "#080F1C",
        borderTop: "1px solid #0f2032",
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 52 }}>
          <p
            style={{
              color: "#2563EB",
              fontWeight: 700,
              letterSpacing: 3,
              fontSize: 11,
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            HELP CENTER
          </p>
          <h2
            style={{
              fontSize: isMobile ? "clamp(22px,6vw,34px)" : "clamp(26px,4vw,42px)",
              fontFamily: "'Cormorant Garant',serif",
              fontWeight: 800,
              color: "#F1F5F9",
              margin: 0,
              letterSpacing: -1,
            }}
          >
            Frequently Asked Questions
          </h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {FAQS.map((faq, i) => (
            <div
              key={i}
              style={{
                background: open === i ? "#0B1E36" : "#071526",
                border: `1px solid ${open === i ? "#2563EB44" : "#132440"}`,
                borderRadius: 12,
                overflow: "hidden",
                transition: "all .25s ease",
              }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: isMobile ? "16px 18px" : "18px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 16,
                }}
              >
                <span
                  style={{
                    color: open === i ? "#E2E8F0" : "#94A3B8",
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 600,
                    textAlign: "left",
                    lineHeight: 1.4,
                  }}
                >
                  {faq.q}
                </span>
                <span
                  style={{
                    color: open === i ? "#2563EB" : "#334155",
                    fontSize: 20,
                    fontWeight: 300,
                    flexShrink: 0,
                    transition: "transform .25s",
                    display: "inline-block",
                    transform: open === i ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                >
                  +
                </span>
              </button>
              {open === i && (
                <div
                  style={{
                    padding: isMobile ? "0 18px 18px" : "0 24px 20px",
                    color: "#64748B",
                    fontSize: 14,
                    lineHeight: 1.75,
                  }}
                >
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  const { isMobile } = useBreakpoint();
  const [open, setOpen] = useState(null);
  return (
    <footer
      style={{
        background: "#040A15",
        borderTop: "1px solid #0f2032",
        padding: isMobile ? "40px 5% 24px" : "64px 6% 32px",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : 48,
            marginBottom: isMobile ? 0 : 52,
            alignItems: "flex-start",
          }}
        >
          {/* Brand */}
          <div
            style={{
              flex: "0 0 auto",
              maxWidth: isMobile ? "100%" : 280,
              marginBottom: isMobile ? 32 : 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 900,
                  fontSize: 17,
                  color: "#fff",
                  padding: 6,
                }}
              >
                <img src="./logo192.png" />
              </div>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#F1F5F9",
                  letterSpacing: -0.5,
                }}
              >
                HEXAFUND
              </span>
            </div>
            <p
              style={{
                color: "#475569",
                fontSize: 14,
                lineHeight: 1.8,
                margin: "0 0 20px",
              }}
            >
              The world's most trusted crowdfunding platform. Registered in
              Delaware, USA.
            </p>
            {/* <div style={{ display:"flex", gap:10 }}>
              {[{label:"𝕏",title:"Twitter/X"},{label:"in",title:"LinkedIn"},{label:"ig",title:"Instagram"},{label:"yt",title:"YouTube"}].map(s=>(
                <button key={s.label} aria-label={s.title} style={{ width:38, height:38, borderRadius:9, background:"#0B1E36", border:"1px solid #132440", display:"flex", alignItems:"center", justifyContent:"center", color:"#475569", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                  {s.label}
                </button>
              ))}
            </div> */}
            {isMobile && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 20,
                  flexWrap: "wrap",
                }}
              >
                {["📱 App Store", "🤖 Google Play"].map((b) => (
                  <div
                    key={b}
                    style={{
                      background: "#0B1E36",
                      border: "1px solid #132440",
                      borderRadius: 10,
                      padding: "9px 16px",
                      color: "#94A3B8",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {b}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Link columns */}
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
              gap: isMobile ? 0 : 32,
            }}
          >
            {FOOTER_COLS.map((col, i) => (
              <div
                key={col.title}
                style={{
                  borderBottom: isMobile ? "1px solid #0f2032" : "none",
                }}
              >
                <button
                  onClick={() =>
                    isMobile ? setOpen(open === i ? null : i) : undefined
                  }
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: isMobile ? "16px 0" : "0",
                    cursor: isMobile ? "pointer" : "default",
                    marginBottom: isMobile ? 0 : 18,
                  }}
                >
                  <span
                    style={{
                      color: "#2563EB",
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    {col.title}
                  </span>
                  {isMobile && (
                    <span
                      style={{
                        color: "#334155",
                        fontSize: 18,
                        transition: "transform .25s",
                        display: "inline-block",
                        transform:
                          open === i ? "rotate(45deg)" : "rotate(0deg)",
                      }}
                    >
                      +
                    </span>
                  )}
                </button>
                {(!isMobile || open === i) && (
                  <div style={{ paddingBottom: isMobile ? 16 : 0 }}>
                    {col.links.map((l) => (
                      <a
                        key={l}
                        href="#"
                        style={{
                          display: "block",
                          color: "#475569",
                          fontSize: 14,
                          marginBottom: 11,
                          textDecoration: "none",
                          lineHeight: 1.4,
                        }}
                        onMouseEnter={(e) => (e.target.style.color = "#7DD3FC")}
                        onMouseLeave={(e) => (e.target.style.color = "#475569")}
                      >
                        {l}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {isMobile && (
          <div
            style={{
              background: "#0B1E36",
              border: "1px solid #132440",
              borderRadius: 16,
              padding: "22px 20px",
              margin: "28px 0",
            }}
          >
            <p
              style={{
                color: "#94A3B8",
                fontSize: 14,
                fontWeight: 600,
                margin: "0 0 12px",
              }}
            >
              📬 Get campaign updates in your inbox
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="email"
                placeholder="your@email.com"
                style={{
                  flex: 1,
                  background: "#071526",
                  border: "1px solid #1e3a5f",
                  color: "#E2E8F0",
                  padding: "11px 14px",
                  borderRadius: 10,
                  fontSize: 14,
                  outline: "none",
                  minWidth: 0,
                }}
              />
              <button
                style={{
                  background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                  color: "#fff",
                  border: "none",
                  padding: "11px 18px",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Subscribe
              </button>
            </div>
          </div>
        )}

        <div style={{ borderTop: "1px solid #0f2032", paddingTop: 22 }}>
          <div
            style={{
              display: "flex",
              gap: isMobile ? 12 : 24,
              flexWrap: "wrap",
              justifyContent: isMobile ? "center" : "flex-start",
              marginBottom: 16,
            }}
          >
            {[
              "🔒 PCI DSS Level 1",
              "✓ SOC 2 Type II",
              "🌍 92 Countries",
              "⚡ Stripe Powered",
            ].map((b) => (
              <span
                key={b}
                style={{ color: "#1e3a5f", fontSize: 12, fontWeight: 600 }}
              >
                {b}
              </span>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              gap: 8,
              alignItems: "center",
            }}
          >
            <p
              style={{
                color: "#1e3a5f",
                fontSize: 13,
                margin: 0,
                textAlign: "center",
              }}
            >
              © 2025 HEXAFUND Inc. All rights reserved.
            </p>
            <div
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {["Privacy", "Terms", "Cookies", "Sitemap"].map((l) => (
                <a
                  key={l}
                  href="#"
                  style={{
                    color: "#1e3a5f",
                    fontSize: 13,
                    textDecoration: "none",
                  }}
                >
                  {l}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── MAIN APP ─── */
export default function App() {
  const { isMobile, isTablet, isDesktop, w } = useBreakpoint();
  const [category, setCategory] = useState("All");
  const [scrollY, setScrollY] = useState(0);
  const [email, setEmail] = useState("");
  const [subbed, setSubbed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    const fn = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // close menu on resize to desktop
  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
  }, [isDesktop]);

  const filtered =
    category === "All"
      ? CAMPAIGNS
      : CAMPAIGNS.filter((c) => c.category === category);

  const scrollTo = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const px = isMobile ? "5%" : isTablet ? "5%" : "6%";

  return (
    <div
      style={{
        fontFamily: "'Outfit','Segoe UI',sans-serif",
        background: "#060E1C",
        color: "#E2E8F0",
        minHeight: "100vh",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Cormorant+Garant:wght@600;700;800&family=Outfit:wght@300;400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes pulse{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:.65;transform:scale(1.07)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}
        .hero-left{animation:fadeUp .8s ease both}
        .hero-right{animation:floatY 6s ease-in-out infinite}
        .nav-link:hover{color:#7DD3FC!important}
        .mob-link:hover{background:#0f2744!important;color:#7DD3FC!important}
        .ham-line{display:block;width:22px;height:2px;background:#94A3B8;border-radius:2px;transition:all .3s ease}
        * { box-sizing: border-box; }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: scrollY > 50 || menuOpen ? "#060E1Cf0" : "transparent",
          backdropFilter: scrollY > 50 || menuOpen ? "blur(22px)" : "none",
          borderBottom: scrollY > 50 || menuOpen ? "1px solid #132440" : "none",
          transition: "background .4s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `0 ${px}`,
            height: isMobile ? 60 : 70,
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: isMobile ? 30 : 36,
                height: isMobile ? 30 : 36,
                borderRadius: 10,
                background: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: isMobile ? 15 : 18,
                color: "#fff",
                padding: isMobile ? 4 : 6,
              }}
            >
              <img src="./logo192.png" />
            </div>
            <span
              style={{
                fontSize: isMobile ? 18 : 22,
                fontWeight: 800,
                letterSpacing: -0.5,
                color: "#F1F5F9",
              }}
            >
              HEXAFUND
            </span>
          </div>

          {/* Desktop nav links */}
          {isDesktop && (
            <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href="#"
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(l.sectionId);
                  }}
                  style={{
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 500,
                    textDecoration: "none",
                    transition: "color .2s",
                  }}
                >
                  {l.label}
                </a>
              ))}
            </div>
          )}

          {/* Desktop CTAs */}
          {isDesktop && (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                style={{
                  background: "transparent",
                  color: "#94A3B8",
                  border: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  padding: "8px 16px",
                }}
                onClick={() => navigate("/login")}
              >
                Log in
              </button>
              <button
                onClick={() => navigate("/register")}
                style={{
                  background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                  color: "#fff",
                  border: "none",
                  padding: "11px 22px",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: "0 4px 20px #2563EB44",
                  whiteSpace: "nowrap",
                }}
              >
                Sign up
              </button>
            </div>
          )}

          {/* Hamburger */}
          {!isDesktop && (
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              style={{
                background: "transparent",
                border: "1px solid #1e3a5f",
                borderRadius: 9,
                padding: "9px 11px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                className="ham-line"
                style={{
                  transform: menuOpen
                    ? "rotate(45deg) translate(5px,5px)"
                    : "none",
                }}
              />
              <span
                className="ham-line"
                style={{ opacity: menuOpen ? 0 : 1 }}
              />
              <span
                className="ham-line"
                style={{
                  transform: menuOpen
                    ? "rotate(-45deg) translate(5px,-5px)"
                    : "none",
                }}
              />
            </button>
          )}
        </div>

        {/* Mobile dropdown */}
        {menuOpen && !isDesktop && (
          <div
            style={{
              animation: "slideDown .25s ease",
              borderTop: "1px solid #132440",
              padding: `12px ${px} 20px`,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                marginBottom: 16,
              }}
            >
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href="#"
                  className="mob-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setMenuOpen(false);
                    scrollTo(l.sectionId);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "13px 16px",
                    borderRadius: 12,
                    color: "#94A3B8",
                    fontSize: 15,
                    fontWeight: 500,
                    textDecoration: "none",
                    transition: "all .2s",
                  }}
                >
                  <span
                    style={{ fontSize: 18, width: 24, textAlign: "center" }}
                  >
                    {l.icon}
                  </span>
                  {l.label}
                  <span
                    style={{
                      marginLeft: "auto",
                      color: "#1e3a5f",
                      fontSize: 16,
                    }}
                  >
                    ›
                  </span>
                </a>
              ))}
            </div>
            <div
              style={{
                height: "1px",
                background: "#132440",
                margin: "4px 0 16px",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                style={{
                  width: "100%",
                  padding: "13px",
                  background: "transparent",
                  border: "1.5px solid #1e3a5f",
                  color: "#94A3B8",
                  borderRadius: 12,
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                }}
                onClick={() => navigate("/login")}
              >
                Log in
              </button>
              <button
                style={{
                  width: "100%",
                  padding: "14px",
                  background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: "pointer",
                  boxShadow: "0 6px 24px #2563EB44",
                }}
                onClick={() => navigate("/register")}
              >
                Join With Hexafund
              </button>
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                justifyContent: "center",
                marginTop: 18,
                flexWrap: "wrap",
              }}
            >
              {["✓ Stripe Secured", "🌍 92 Countries", "✓ Zero Fees"].map(
                (b) => (
                  <span
                    key={b}
                    style={{ color: "#334155", fontSize: 12, fontWeight: 500 }}
                  >
                    {b}
                  </span>
                ),
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: isMobile
            ? `${80}px ${px} 48px`
            : isTablet
              ? `90px ${px} 60px`
              : `90px ${px} 60px`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "15%",
            right: "8%",
            width: isMobile ? 250 : 500,
            height: isMobile ? 250 : 500,
            borderRadius: "50%",
            background: "radial-gradient(circle,#1D4ED833 0%,transparent 70%)",
            pointerEvents: "none",
            animation: "pulse 8s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "5%",
            left: "5%",
            width: isMobile ? 200 : 380,
            height: isMobile ? 200 : 380,
            borderRadius: "50%",
            background: "radial-gradient(circle,#22D3EE0D 0%,transparent 70%)",
            pointerEvents: "none",
            animation: "pulse 11s ease-in-out infinite 2s",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(#13244022 1px,transparent 1px),linear-gradient(90deg,#13244022 1px,transparent 1px)",
            backgroundSize: isMobile ? "50px 50px" : "80px 80px",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            width: "100%",
            display: "grid",
            gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr",
            gap: isDesktop ? 80 : 40,
            alignItems: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left */}
          <div className="hero-left">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#1D4ED822",
                border: "1px solid #2563EB55",
                borderRadius: 99,
                padding: "5px 14px",
                marginBottom: isMobile ? 20 : 28,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#22D3EE",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  color: "#7DD3FC",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 700,
                  letterSpacing: isMobile ? 1 : 1.5,
                  textTransform: "uppercase",
                }}
              >
                Over $1.2 Billion Raised Globally
              </span>
            </div>
            <h1
              style={{
                fontSize: isMobile
                  ? "clamp(36px,9vw,48px)"
                  : isTablet
                    ? "clamp(40px,6vw,58px)"
                    : "clamp(42px,5vw,68px)",
                fontWeight: 800,
                fontFamily: "'Cormorant Garant',Georgia,serif",
                lineHeight: 1.08,
                margin: `0 0 ${isMobile ? 18 : 24}px`,
                color: "#F8FAFC",
                letterSpacing: -1,
              }}
            >
              Fund Bold{" "}
              <span
                style={{
                  background: "linear-gradient(90deg,#60A5FA,#22D3EE)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Ideas.
              </span>
              <br />
              Fuel Global{" "}
              <span
                style={{
                  background: "linear-gradient(90deg,#22D3EE,#818CF8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Change.
              </span>
            </h1>
            <p
              style={{
                fontSize: isMobile ? 15 : 17,
                color: "#94A3B8",
                lineHeight: 1.75,
                maxWidth: 520,
                margin: `0 0 ${isMobile ? 28 : 40}px`,
              }}
            >
              HEXAFUND is the world's most trusted crowdfunding platform —
              connecting creators and innovators with millions of backers across
              92 countries.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: isMobile ? 28 : 40,
              }}
            >
              <button
                style={{
                  background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                  color: "#fff",
                  border: "none",
                  padding: isMobile ? "13px 24px" : "16px 36px",
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: isMobile ? 14 : 16,
                  cursor: "pointer",
                  boxShadow: "0 8px 32px #2563EB55",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "120px" : "auto",
                }}
              >
                Launch Your Campaign
              </button>
              <button
                style={{
                  background: "transparent",
                  color: "#94A3B8",
                  border: "1.5px solid #1e3a5f",
                  padding: isMobile ? "13px 20px" : "16px 36px",
                  borderRadius: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 16,
                  cursor: "pointer",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "100px" : "auto",
                }}
              >
                ▶ How It Works
              </button>
            </div>
            <div
              style={{
                display: "flex",
                gap: isMobile ? 16 : 24,
                flexWrap: "wrap",
              }}
            >
              {[
                "Stripe-Powered Payments",
                "92 Country Support",
                "Zero Hidden Fees",
              ].map((f) => (
                <div
                  key={f}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span style={{ color: "#22D3EE", fontSize: 15 }}>✓</span>
                  <span
                    style={{
                      color: "#64748B",
                      fontSize: isMobile ? 12 : 13,
                      fontWeight: 500,
                    }}
                  >
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — floating card (hidden on mobile, shown on tablet/desktop) */}
          {!isMobile && (
            <div style={{ position: "relative" }} className="hero-right">
              <div
                style={{
                  background: "#0B1E36",
                  border: "1px solid #1e3a5f",
                  borderRadius: 24,
                  overflow: "hidden",
                  boxShadow: "0 40px 100px #0009,0 0 0 1px #2563EB22",
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=700&q=80"
                  alt="dashboard"
                  style={{
                    width: "100%",
                    height: 190,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <div style={{ padding: "20px 22px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 10,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#F1F5F9",
                          fontWeight: 700,
                          fontSize: 15,
                          marginBottom: 2,
                        }}
                      >
                        NovaBike Pro Campaign
                      </div>
                      <div style={{ color: "#64748B", fontSize: 12 }}>
                        🇩🇪 Berlin, Germany
                      </div>
                    </div>
                    <Tag text="Staff Pick" color="#22D3EE" />
                  </div>
                  <ProgressBar raised={284500} goal={350000} />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 10,
                    }}
                  >
                    {[
                      ["$284K", "Raised"],
                      ["3,142", "Backers"],
                      ["18 days", "Remaining"],
                    ].map(([v, l]) => (
                      <div
                        key={l}
                        style={{
                          background: "#071526",
                          borderRadius: 10,
                          padding: "10px 12px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: "#7DD3FC",
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {v}
                        </div>
                        <div style={{ color: "#475569", fontSize: 11 }}>
                          {l}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Floating badges — only on desktop so they don't get clipped */}
              {isDesktop && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: -16,
                      right: -16,
                      background: "#0B1E36",
                      border: "1px solid #22D3EE44",
                      borderRadius: 14,
                      padding: "10px 16px",
                      boxShadow: "0 8px 24px #22D3EE22",
                    }}
                  >
                    <div
                      style={{
                        color: "#22D3EE",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      🌍 92 Countries
                    </div>
                    <div style={{ color: "#475569", fontSize: 11 }}>
                      Active backers right now
                    </div>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: -14,
                      left: -18,
                      background: "#0B1E36",
                      border: "1px solid #4ADE8044",
                      borderRadius: 14,
                      padding: "10px 16px",
                      boxShadow: "0 8px 24px #4ADE8022",
                    }}
                  >
                    <div
                      style={{
                        color: "#4ADE80",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      ✓ Fully Funded!
                    </div>
                    <div style={{ color: "#475569", fontSize: 11 }}>
                      MindStack · 5 min ago
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile hero mini-card (inline, no absolute positioning) */}
          {isMobile && (
            <div
              style={{
                background: "#0B1E36",
                border: "1px solid #1e3a5f",
                borderRadius: 18,
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "16px 18px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#F1F5F9",
                        fontWeight: 700,
                        fontSize: 14,
                        marginBottom: 2,
                      }}
                    >
                      NovaBike Pro Campaign
                    </div>
                    <div style={{ color: "#64748B", fontSize: 11 }}>
                      🇩🇪 Berlin, Germany
                    </div>
                  </div>
                  <Tag text="Staff Pick" color="#22D3EE" />
                </div>
                <ProgressBar raised={284500} goal={350000} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 8,
                  }}
                >
                  {[
                    ["$284K", "Raised"],
                    ["3,142", "Backers"],
                    ["18d", "Left"],
                  ].map(([v, l]) => (
                    <div
                      key={l}
                      style={{
                        background: "#071526",
                        borderRadius: 8,
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          color: "#7DD3FC",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {v}
                      </div>
                      <div style={{ color: "#475569", fontSize: 10 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── PLATFORM STATS BAR ── */}
      <section
        style={{
          padding: `20px ${px}`,
          borderTop: "1px solid #0f2032",
          borderBottom: "1px solid #0f2032",
          background: "#080F1C",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)",
            gap: isMobile ? 16 : 0,
          }}
        >
          {PLATFORM_STATS.map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                justifyContent: "center",
                padding: "8px 12px",
                borderRight: !isMobile && i < 3 ? "1px solid #132440" : "none",
              }}
            >
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <span
                style={{
                  color: "#7DD3FC",
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 700,
                }}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#060E1C",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : isTablet
                ? "repeat(2,1fr)"
                : "repeat(4,1fr)",
            background: "#0B1E36",
            borderRadius: 20,
            border: "1px solid #132440",
            overflow: "hidden",
          }}
        >
          {STATS.map((s, i) => {
            const last = i === STATS.length - 1;
            const isRight = isMobile ? i % 2 === 1 : i === 3;
            const isBottom = isMobile && i >= 2;
            return (
              <div
                key={i}
                style={{
                  padding: isMobile ? "24px 16px" : "36px 24px",
                  textAlign: "center",
                  borderRight: isRight ? "none" : "1px solid #132440",
                  borderBottom:
                    isMobile && !isBottom ? "1px solid #132440" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? 22 : 30,
                    fontWeight: 800,
                    fontFamily: "'Cormorant Garant',serif",
                    color: "#60A5FA",
                    marginBottom: 6,
                    letterSpacing: -1,
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    color: "#475569",
                    fontSize: isMobile ? 11 : 13,
                    fontWeight: 500,
                  }}
                >
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section
        id="how-it-works"
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#080F1C",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{ textAlign: "center", marginBottom: isMobile ? 36 : 52 }}
          >
            <p
              style={{
                color: "#2563EB",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: 11,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              PROCESS
            </p>
            <h2
              style={{
                fontSize: isMobile
                  ? "clamp(24px,7vw,36px)"
                  : "clamp(28px,4vw,44px)",
                fontFamily: "'Cormorant Garant',serif",
                fontWeight: 800,
                color: "#F1F5F9",
                margin: 0,
                letterSpacing: -1,
              }}
            >
              Launch in Four Steps
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "1fr 1fr"
                  : "repeat(4,1fr)",
              gap: 16,
            }}
          >
            {[
              {
                n: "01",
                icon: "🖊",
                title: "Craft Your Story",
                desc: "Build a stunning campaign page with rich media, reward tiers, and your funding target.",
              },
              {
                n: "02",
                icon: "⚡",
                title: "Go Live in Minutes",
                desc: "Our approval process takes under 24 hours. Then go global immediately.",
              },
              {
                n: "03",
                icon: "🌐",
                title: "Reach Global Backers",
                desc: "HEXAFUND's discovery engine puts your project in front of the right audience worldwide.",
              },
              {
                n: "04",
                icon: "💵",
                title: "Receive Your Funds",
                desc: "Funds in 135 currencies, straight to your bank. No middlemen, no surprises.",
              },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  background: "#0B1E36",
                  border: "1px solid #132440",
                  borderRadius: 16,
                  padding: "28px 22px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 18,
                    fontSize: isMobile ? 40 : 52,
                    fontWeight: 900,
                    color: "#2563EB0A",
                    fontFamily: "'Cormorant Garant',serif",
                    lineHeight: 1,
                  }}
                >
                  {item.n}
                </div>
                <div style={{ fontSize: 28, marginBottom: 12 }}>
                  {item.icon}
                </div>
                <h3
                  style={{
                    color: "#E2E8F0",
                    fontSize: isMobile ? 15 : 17,
                    fontWeight: 700,
                    marginBottom: 10,
                    fontFamily: "'Cormorant Garant',serif",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    color: "#64748B",
                    fontSize: 14,
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEDICAL CATEGORIES ── */}
      <section
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#060E1C",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 52 }}>
            <p
              style={{
                color: "#2563EB",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: 11,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              MEDICAL FUNDRAISING
            </p>
            <h2
              style={{
                fontSize: isMobile ? "clamp(22px,6vw,34px)" : "clamp(26px,4vw,42px)",
                fontFamily: "'Cormorant Garant',serif",
                fontWeight: 800,
                color: "#F1F5F9",
                margin: "0 0 14px",
                letterSpacing: -1,
              }}
            >
              Raise Funds For Medical Treatment On HEXAFUND
            </h2>
            <p style={{ color: "#64748B", fontSize: isMobile ? 14 : 15, maxWidth: 560, margin: "0 auto" }}>
              Get emergency financial help to pay hospital bills and medication bills with online fundraising.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr 1fr"
                : isTablet
                  ? "repeat(3,1fr)"
                  : "repeat(5,1fr)",
              gap: 16,
            }}
          >
            {MEDICAL_CATEGORIES.map((cat, i) => (
              <div
                key={i}
                style={{
                  background: "#0B1E36",
                  border: `1px solid ${cat.color}33`,
                  borderRadius: 16,
                  padding: isMobile ? "20px 14px" : "28px 18px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all .3s ease",
                  borderTop: `3px solid ${cat.color}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = `0 16px 40px ${cat.color}22`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ fontSize: isMobile ? 30 : 38, marginBottom: 12 }}>{cat.icon}</div>
                <div
                  style={{
                    color: "#E2E8F0",
                    fontWeight: 700,
                    fontSize: isMobile ? 13 : 14,
                    marginBottom: 6,
                    fontFamily: "'Cormorant Garant',serif",
                  }}
                >
                  {cat.title}
                </div>
                <div style={{ color: "#475569", fontSize: 11 }}>Maximum Raised</div>
                <div style={{ color: cat.color, fontWeight: 800, fontSize: 13, marginTop: 4 }}>
                  {cat.raised}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAMPAIGNS ── */}
      <section
        id="discover"
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#060E1C",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "flex-end",
              marginBottom: isMobile ? 20 : 32,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <p
                style={{
                  color: "#2563EB",
                  fontWeight: 700,
                  letterSpacing: 3,
                  fontSize: 11,
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                EXPLORE
              </p>
              <h2
                style={{
                  fontSize: isMobile
                    ? "clamp(24px,7vw,36px)"
                    : "clamp(28px,4vw,44px)",
                  fontFamily: "'Cormorant Garant',serif",
                  fontWeight: 800,
                  color: "#F1F5F9",
                  margin: 0,
                  letterSpacing: -1,
                }}
              >
                Campaigns Around the World
              </h2>
            </div>
            <a
              href="#"
              style={{
                color: "#60A5FA",
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              See all →
            </a>
          </div>

          {/* Category filters - horizontally scrollable on mobile */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 24,
              overflowX: "auto",
              paddingBottom: 4,
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
            }}
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                style={{
                  background:
                    category === cat
                      ? "linear-gradient(90deg,#1D4ED8,#2563EB)"
                      : "#0B1E36",
                  color: category === cat ? "#fff" : "#64748B",
                  border: `1px solid ${category === cat ? "transparent" : "#132440"}`,
                  padding: "8px 16px",
                  borderRadius: 99,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all .2s",
                  boxShadow: category === cat ? "0 4px 16px #2563EB44" : "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2,1fr)"
                  : "repeat(3,1fr)",
              gap: 20,
            }}
          >
            {filtered.map((c) => (
              <CampaignCard key={c.id} c={c} />
            ))}
          </div>
        </div>
      </section>

      {/* ── GLOBAL STATS ── */}
      <section
        id="enterprise"
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#080F1C",
          borderTop: "1px solid #0f2032",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr",
            gap: isDesktop ? 64 : 40,
            alignItems: "center",
          }}
        >
          <div>
            <p
              style={{
                color: "#2563EB",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: 11,
                textTransform: "uppercase",
                marginBottom: 14,
              }}
            >
              GLOBAL REACH
            </p>
            <h2
              style={{
                fontSize: isMobile
                  ? "clamp(24px,7vw,36px)"
                  : "clamp(26px,3.5vw,44px)",
                fontFamily: "'Cormorant Garant',serif",
                fontWeight: 800,
                color: "#F1F5F9",
                margin: "0 0 18px",
                letterSpacing: -1,
                lineHeight: 1.1,
              }}
            >
              Borderless Funding for a Connected World
            </h2>
            <p
              style={{
                color: "#64748B",
                fontSize: isMobile ? 14 : 15,
                lineHeight: 1.8,
                marginBottom: 24,
              }}
            >
              Whether your backers are in São Paulo, Stockholm, or Singapore —
              HEXAFUND handles multi-currency pledges, local tax compliance, and
              instant payouts in 135+ currencies.
            </p>
            {[
              { flag: "🇺🇸", label: "United States", amount: "$412M funded" },
              { flag: "🇬🇧", label: "United Kingdom", amount: "$187M funded" },
              { flag: "🇩🇪", label: "Germany", amount: "$143M funded" },
              { flag: "🇦🇺", label: "Australia", amount: "$98M funded" },
            ].map((r) => (
              <div
                key={r.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom: "1px solid #0f2032",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 18 }}>{r.flag}</span>
                  <span
                    style={{ color: "#94A3B8", fontSize: 14, fontWeight: 500 }}
                  >
                    {r.label}
                  </span>
                </div>
                <span
                  style={{ color: "#60A5FA", fontSize: 14, fontWeight: 700 }}
                >
                  {r.amount}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            {[
              {
                title: "Avg. Campaign",
                value: "$47K",
                sub: "raised per funded project",
                color: "#2563EB",
              },
              {
                title: "Success Rate",
                value: "89%",
                sub: "of campaigns hit their target",
                color: "#22D3EE",
              },
              {
                title: "Fastest Funded",
                value: "4 hrs",
                sub: "record time to 100% goal",
                color: "#818CF8",
              },
              {
                title: "Repeat Creators",
                value: "62%",
                sub: "come back for a 2nd campaign",
                color: "#4ADE80",
              },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  background: "#0B1E36",
                  border: "1px solid #132440",
                  borderRadius: 16,
                  padding: isMobile ? "18px 14px" : "24px 20px",
                  borderTop: `3px solid ${s.color}`,
                }}
              >
                <div
                  style={{
                    color: "#475569",
                    fontSize: 11,
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    color: "#F1F5F9",
                    fontWeight: 800,
                    fontSize: isMobile ? 20 : 26,
                    fontFamily: "'Cormorant Garant',serif",
                    marginBottom: 4,
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    color: "#475569",
                    fontSize: isMobile ? 11 : 12,
                    lineHeight: 1.5,
                  }}
                >
                  {s.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY FUNDRAISE ── */}
      <section
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#060E1C",
          borderTop: "1px solid #0f2032",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 52 }}>
            <p
              style={{
                color: "#2563EB",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: 11,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              PLATFORM BENEFITS
            </p>
            <h2
              style={{
                fontSize: isMobile ? "clamp(22px,6vw,34px)" : "clamp(26px,4vw,42px)",
                fontFamily: "'Cormorant Garant',serif",
                fontWeight: 800,
                color: "#F1F5F9",
                margin: 0,
                letterSpacing: -1,
              }}
            >
              Why Fundraise With HEXAFUND?
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3,1fr)",
              gap: 20,
            }}
          >
            {[
              {
                icon: "0%",
                iconBg: "#1D4ED822",
                iconColor: "#60A5FA",
                title: "0% Platform Fee",
                desc: "Start a fundraiser for free. We charge absolutely zero platform fees so every rupee you raise goes directly to you.",
                badge: "No Hidden Charges",
              },
              {
                icon: "📱",
                iconBg: "#7C3AED22",
                iconColor: "#A78BFA",
                title: "Free Fundraising Mobile App",
                desc: "Manage your fundraiser on the go with our powerful mobile app available for both iOS and Android.",
                badge: "iOS & Android",
              },
              {
                icon: "👥",
                iconBg: "#06524422",
                iconColor: "#34D399",
                title: "50+ Lakh Donor Community",
                desc: "Tap into our vast network of verified donors who are ready to support genuine medical and social causes.",
                badge: "Verified Donors",
              },
            ].map((f, i) => (
              <div
                key={i}
                style={{
                  background: "#0B1E36",
                  border: "1px solid #132440",
                  borderRadius: 20,
                  padding: isMobile ? "28px 20px" : "36px 28px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: f.iconBg,
                    border: `2px solid ${f.iconColor}44`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                    fontSize: f.icon === "0%" ? 22 : 30,
                    fontWeight: 900,
                    color: f.iconColor,
                  }}
                >
                  {f.icon}
                </div>
                <div
                  style={{
                    background: f.iconBg,
                    color: f.iconColor,
                    border: `1px solid ${f.iconColor}44`,
                    borderRadius: 99,
                    padding: "3px 12px",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    display: "inline-block",
                    marginBottom: 14,
                  }}
                >
                  {f.badge}
                </div>
                <h3
                  style={{
                    color: "#E2E8F0",
                    fontSize: isMobile ? 17 : 19,
                    fontWeight: 700,
                    marginBottom: 12,
                    fontFamily: "'Cormorant Garant',serif",
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ color: "#64748B", fontSize: 14, lineHeight: 1.75, margin: 0 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <button
              onClick={() => navigate("/register")}
              style={{
                background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                color: "#fff",
                border: "none",
                padding: isMobile ? "14px 32px" : "16px 48px",
                borderRadius: 12,
                fontWeight: 700,
                fontSize: isMobile ? 15 : 16,
                cursor: "pointer",
                boxShadow: "0 8px 32px #2563EB44",
              }}
            >
              Start a Free Fundraiser
            </button>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section
        id="for-creators"
        style={{
          padding: `${isMobile ? 48 : 80}px ${px}`,
          background: "#060E1C",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{ textAlign: "center", marginBottom: isMobile ? 32 : 48 }}
          >
            <p
              style={{
                color: "#2563EB",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: 11,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              COMMUNITY
            </p>
            <h2
              style={{
                fontSize: isMobile
                  ? "clamp(24px,7vw,36px)"
                  : "clamp(28px,4vw,44px)",
                fontFamily: "'Cormorant Garant',serif",
                fontWeight: 800,
                color: "#F1F5F9",
                margin: 0,
                letterSpacing: -1,
              }}
            >
              Trusted by Creators Worldwide
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2,1fr)"
                  : "repeat(3,1fr)",
              gap: 20,
            }}
          >
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                style={{
                  background: "#0B1E36",
                  border: "1px solid #132440",
                  borderRadius: 18,
                  padding: isMobile ? "22px 20px" : "32px 28px",
                }}
              >
                <div style={{ display: "flex", gap: 3, marginBottom: 16 }}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} style={{ color: "#FBBF24", fontSize: 14 }}>
                      ★
                    </span>
                  ))}
                </div>
                <p
                  style={{
                    color: "#94A3B8",
                    fontSize: isMobile ? 14 : 15,
                    lineHeight: 1.8,
                    margin: "0 0 24px",
                    fontStyle: "italic",
                  }}
                >
                  "{t.text}"
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg,#1D4ED8,#22D3EE)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 14,
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <div
                      style={{
                        color: "#E2E8F0",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      {t.name}
                    </div>
                    <div style={{ color: "#475569", fontSize: 12 }}>
                      {t.role} · {t.location}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        id="cta"
        style={{
          padding: `${isMobile ? 64 : 100}px ${px}`,
          background: "#080F1C",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at center,#1D4ED822 0%,transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(#132440 1px,transparent 1px),linear-gradient(90deg,#132440 1px,transparent 1px)",
            backgroundSize: "60px 60px",
            opacity: 0.18,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 700,
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: isMobile
                ? "clamp(28px,8vw,42px)"
                : "clamp(30px,5vw,56px)",
              fontFamily: "'Cormorant Garant',serif",
              fontWeight: 800,
              color: "#F8FAFC",
              marginBottom: 16,
              letterSpacing: -1,
              lineHeight: 1.1,
            }}
          >
            Your Idea Deserves
            <br />a Global Audience
          </h2>
          <p
            style={{
              color: "#64748B",
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.75,
              marginBottom: 36,
            }}
          >
            Join 24,000+ creators who launched with HEXAFUND. No upfront costs.
            No risk. Just results.
          </p>
          {/* {!subbed ? (
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  background: "#0B1E36",
                  border: "1px solid #1e3a5f",
                  color: "#E2E8F0",
                  padding: "14px 20px",
                  borderRadius: 12,
                  fontSize: 15,
                  width: isMobile ? "100%" : 300,
                  outline: "none",
                  maxWidth: "100%",
                }}
              />
              <button
                onClick={() => email && setSubbed(true)}
                style={{
                  background: "linear-gradient(135deg,#1D4ED8,#2563EB)",
                  color: "#fff",
                  border: "none",
                  padding: "14px 28px",
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: "pointer",
                  boxShadow: "0 8px 24px #2563EB44",
                  whiteSpace: "nowrap",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Get Early Access →
              </button>
            </div>
          ) : (
            <div
              style={{
                background: "#0B1E36",
                border: "1px solid #22D3EE44",
                borderRadius: 16,
                padding: "20px 32px",
                display: "inline-block",
                color: "#22D3EE",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              ✓ You're on the list! We'll be in touch.
            </div>
          )}
          <p style={{ color: "#334155", fontSize: 12, marginTop: 16 }}>
            No spam. Unsubscribe anytime. We respect your privacy.
          </p> */}
        </div>
      </section>

      {/* ── FAQ ── */}
      <FaqSection isMobile={isMobile} px={px} />

      <Footer />
    </div>
  );
}
